import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { Swords } from "lucide-react";
import { usePageView } from "@/hooks/usePageView";
import { guideSignals } from "@/lib/guideSignals";
import { z } from "zod/v4";
import { EvidenceMapPanel } from "@/components/expand/EvidenceMapPanel";
import { EvidenceSourceDrawer } from "@/components/expand/EvidenceSourceDrawer";
import { FieldDebateView } from "@/components/analyze/FieldDebateView";
import { CompletenessMatrix } from "@/components/evidence/CompletenessMatrix";
import { ArgumentCritiquePanel } from "@/components/build/ArgumentCritiquePanel";
import { BuildOverview } from "@/components/build/BuildOverview";
import { effectiveBuildView } from "@/lib/buildNextStep";
import { useClaims } from "@/hooks/useClaims";
import { useCurrentUser, isPaidPlan } from "@/hooks/useCurrentUser";
import { useSpotlight } from "@/hooks/useSpotlight";
import { useFieldDebate } from "@/hooks/useFieldDebate";
import { useDebateStore } from "@/stores/debate-store";
import { useEvidenceStore } from "@/stores/evidence-store";

const WritingEditor = lazy(() =>
  import("@/components/writing/WritingEditor").then((m) => ({ default: m.WritingEditor })),
);

const searchSchema = z.object({
  view: z.enum(["overview", "map", "write", "split", "matrix"]).optional(),
  critique: z.boolean().optional(),
});

export const Route = createFileRoute("/concepts/$conceptId/build")({
  component: EvidencePage,
  validateSearch: (search) => searchSchema.parse(search),
});

function EvidencePage() {
  usePageView("/concepts/evidence");
  const { conceptId } = Route.useParams();
  const search = Route.useSearch();
  const { data: claimsData } = useClaims(conceptId);
  const { data: debateMap, isLoading: debateLoading } = useFieldDebate(conceptId);
  const { data: currentUser } = useCurrentUser();
  const isPaid = isPaidPlan(currentUser?.plan);
  // Smart default: first visit (zero claims) lands on Overview; returning
  // users land on Map (canvas-first). null while claims resolve — render a
  // quiet placeholder rather than flashing the wrong view.
  const view = effectiveBuildView(
    search.view,
    claimsData === undefined ? undefined : claimsData.length,
  );
  const navigate = Route.useNavigate();

  // Latch the resolved smart default into the URL (replace) so later
  // claim-count changes can't re-route the view under the user — e.g.
  // deleting the last claim on the canvas must not yank Map → Overview.
  useEffect(() => {
    if (search.view || view === null) return;
    navigate({ search: (prev) => ({ ...prev, view }), replace: true });
  }, [search.view, view, navigate]);

  // Consume pending-spotlight key left by JourneyBar's Show Me handler
  useSpotlight();

  const handleShowClaim = (claimId: string) => {
    useEvidenceStore.getState().selectClaim(claimId);
    if (view !== "map" && view !== "split") {
      navigate({ search: (prev) => ({ ...prev, view: "map" }) });
    }
  };

  const setHighlightedPapers = useDebateStore((s) => s.setHighlightedPapers);
  const setHighlightedClaim = useDebateStore((s) => s.setHighlightedClaim);
  const setHighlightedCamp = useDebateStore((s) => s.setHighlightedCamp);
  const highlightTimerRef = useRef<ReturnType<typeof setTimeout>>(undefined);

  // Clean up highlight timer on unmount
  useEffect(() => () => clearTimeout(highlightTimerRef.current), []);

  // Track writing workspace opens for guide milestones
  useEffect(() => {
    if (view === "write") {
      guideSignals.mark(conceptId, "hasOpenedWritingWorkspace");
    }
  }, [view, conceptId]);

  const [sourceView, setSourceView] = useState<{
    paperId: string;
    paperTitle: string;
    excerptText: string;
    pageNumber?: number;
  } | null>(null);

  return (
    <div className="relative flex flex-col" style={{ height: "calc(100vh - 200px)", minHeight: 400 }}>
      {search.critique && (
        <ArgumentCritiquePanel
          conceptId={conceptId}
          onClose={() =>
            navigate({ search: (prev) => ({ ...prev, critique: undefined }) })
          }
          onShowClaim={handleShowClaim}
        />
      )}
      {/* Write view — full-height editor (paid only) */}
      {view === "write" && isPaid && (
        <Suspense fallback={<div className="flex h-full items-center justify-center"><p className="text-muted-foreground">Loading editor...</p></div>}>
          <WritingEditor conceptId={conceptId} />
        </Suspense>
      )}
      {view === "write" && !isPaid && (
        <div className="flex h-full items-center justify-center">
          <div className="max-w-md rounded-lg border border-purple-200 bg-purple-50 p-6 text-center">
            <h3 className="text-lg font-semibold text-purple-900">Writing Workspace is a paid feature</h3>
            <p className="mt-2 text-sm text-purple-700">Upgrade to a paid plan to use the writing workspace.</p>
          </div>
        </div>
      )}

      {/* Content — mutually exclusive views */}
      {view === null ? (
        /* Claims still resolving — quiet placeholder, no flash */
        <div className="flex flex-1 items-center justify-center" />
      ) : view === "overview" ? (
        <div className="flex-1 min-h-0">
          <BuildOverview conceptId={conceptId} />
        </div>
      ) : view === "matrix" ? (
        <div className="flex-1 min-h-0">
          <CompletenessMatrix conceptId={conceptId} />
        </div>
      ) : view === "split" && !debateMap ? (
        /* Direct/stale ?view=split URL with no debate map — the rail locks
           this row, but nothing stops the URL itself. Render an explicit
           gate instead of a stranded half-width map. (Quiet while the
           debate query is still loading to avoid a flash.) */
        <div className="flex flex-1 items-center justify-center">
          {!debateLoading && (
            <div className="max-w-md rounded-lg border border-border p-6 text-center">
              <h3 className="text-base font-semibold">Split view needs a Field Debate</h3>
              <p className="mt-2 text-sm text-muted-foreground">
                Split shows your evidence map beside the field's camps and
                tensions. Generate a Field Debate on the Analyze page first.
              </p>
              <Link
                to="/concepts/$conceptId/analyze"
                params={{ conceptId }}
                search={{ activeTool: "debate" }}
                className="mt-4 inline-flex items-center gap-1.5 rounded-md bg-accent px-3 py-1.5 text-sm font-medium hover:bg-accent/80"
              >
                <Swords className="size-3.5" /> Open Field Debate
              </Link>
            </div>
          )}
        </div>
      ) : view === "split" ? (
        <div className="flex flex-1 min-h-0">
          {/* Evidence Map pane */}
          <div className="w-1/2 border-r border-border">
            <EvidenceMapPanel
              conceptId={conceptId}
              isOpen={true}
              onToggle={() => {}}
              claimCount={claimsData?.length ?? 0}
              mode="fullpage"
              onViewSource={(paperId, paperTitle, excerptText, pageNumber) =>
                setSourceView({ paperId, paperTitle, excerptText, pageNumber })
              }
            />
          </div>

          {/* Field Debate pane */}
          {debateMap && (
            <div className="w-1/2 flex flex-col">
              <div className="flex-1 min-h-0">
                <FieldDebateView
                  debateMap={debateMap}
                  conceptId={conceptId}
                  compact
                  onCampClick={(campId, paperIds) => {
                    clearTimeout(highlightTimerRef.current);
                    setHighlightedPapers(paperIds);
                    setHighlightedCamp(campId);
                    highlightTimerRef.current = setTimeout(() => {
                      setHighlightedPapers(null);
                      setHighlightedCamp(null);
                    }, 3000);
                  }}
                  onClaimClick={(claimId) => {
                    clearTimeout(highlightTimerRef.current);
                    setHighlightedClaim(claimId);
                    highlightTimerRef.current = setTimeout(() => setHighlightedClaim(null), 3000);
                  }}
                />
              </div>
            </div>
          )}
        </div>
      ) : view === "write" ? (
        /* Write view already rendered above — nothing here */
        null
      ) : (
        /* Map view (default) */
        <div className="flex flex-1 min-h-0">
          {/* Evidence Map pane */}
          <div className={sourceView ? "flex-1" : "w-full"}>
            <EvidenceMapPanel
              conceptId={conceptId}
              isOpen={true}
              onToggle={() => {}}
              claimCount={claimsData?.length ?? 0}
              mode="fullpage"
              onViewSource={(paperId, paperTitle, excerptText, pageNumber) =>
                setSourceView({ paperId, paperTitle, excerptText, pageNumber })
              }
            />
          </div>

          {/* Source drawer */}
          {sourceView && (
            <EvidenceSourceDrawer
              open={!!sourceView}
              onClose={() => setSourceView(null)}
              conceptId={conceptId}
              paperId={sourceView.paperId}
              paperTitle={sourceView.paperTitle}
              excerptText={sourceView.excerptText}
              pageNumber={sourceView.pageNumber}
            />
          )}
        </div>
      )}
    </div>
  );
}
